import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import shallowEqual from 'shallowequal';

import { Order } from '../../../ts/helpers/Order';
import { ContentFilterKeyName } from '../../../ts/models/ContentFilters';
import { areValid, getDefaultOrderForSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { setContentFilterNoRequestContent } from '../../actions/spaces/filters/FilterActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { SpaceInfo } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps } from '../../utils/ConnectUtils';

export interface ExplorerSubspaceInitializerOwnProps {
  readonly routeLocationSearch: string;
  readonly spaceInfo: SpaceInfo;
}

export interface ExplorerSubspaceInitializerDispatchProps {
  readonly contentCountsUpToDate: () => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly setContentFilterNoRequestContent: (filterType: ContentFilterKeyName, value: string) => void;
  readonly requestSpaceContents: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
}

export type ExplorerSubspaceInitializerProps = ExplorerSubspaceInitializerOwnProps & ExplorerSubspaceInitializerDispatchProps;

export interface ExplorerSubspaceContainerState {
  readonly renderChildren: boolean;
}


/**
 * This component wraps an Explorer for a particular space/subspace to provide it with the necessary URL bindings and Redux actions
 * that will properly initialize the explorer for the particular subspace. It should be rendered as a child component of a React <Route>,
 * since it requires the location.search value that is watched by the router.
 *
 * Depending on the structure of the space, this component may be a parent of either a space or a subspace component.
 * It can be situated anywhere below the router and above the explorer.
 *
 * This component is intended for explorer *subspaces*. For a space that requires its own initialization at the space level (e.g. fetching
 * a workbook for the workbook space), that initialization should be done in a provider further up the component hierarchy, or
 * write a custom provider that combines the space and subspace initialization.
 */
export class ExplorerSubspaceInitializer extends React.PureComponent<ExplorerSubspaceInitializerProps, ExplorerSubspaceContainerState> {

  public constructor(props: ExplorerSubspaceInitializerProps) {
    super(props);
    this.state = {renderChildren: false};
  }

  public render() {
    return this.state.renderChildren && <>{this.props.children}</>;
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
    this.setState({renderChildren: true});
  }

  public componentDidUpdate(prevProps: ExplorerSubspaceInitializerProps) {
    if (!shallowEqual(prevProps.spaceInfo, this.props.spaceInfo)) {
      this.changeSubspace();
    }
  }

  private changeSubspace() {
    this.props.enterSpace(this.props.spaceInfo);
    const currentQueryValues = queryString.parse(this.props.routeLocationSearch);

    this.props.contentCountsUpToDate();

    // set default sort order if order url params are empty or invalid
    // Ensures redux store contains a valid sort
    let order = Order.fromInput(currentQueryValues[UrlParamKeys.Order]);
    if (!order || !areValid(order, [], this.props.spaceInfo)) {
      order = getDefaultOrderForSpaceInfo(this.props.spaceInfo);
    }

    this.props.setOrderAction(order);
    this.props.requestSpaceContents();
  }
}

const mapDispatchToProps: MapDispatchToProps<ExplorerSubspaceInitializerDispatchProps> = (dispatch) => bindActionCreators({
  setContentFilterNoRequestContent,
  contentCountsUpToDate,
  requestSpaceContents,
  enterSpace,
  setOrderAction,
}, dispatch);

export const ConnectedExplorerSubspaceInitializer: React.ComponentClass<ExplorerSubspaceInitializerOwnProps> =
    connect(undefined, mapDispatchToProps)(ExplorerSubspaceInitializer);
